import {
  MouseEvent,
  ReactNode,
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import useElementSize from '@/hooks/useElementSize'
import useWindowSize from '@/hooks/useWindowSize'

import Picture from '../picture'
import ArrowLeft from '../svg/ArrowLeft'

interface ImageItem {
  image: string
  alt: string
  title?: ReactNode
}

interface Props {
  imageList: ImageItem[]
  aspectRatio: number
  desktopImageCount: number
  mobileImageCount: number
}

interface CarouselImageItemProps {
  image: string
  alt: string
  imageWidth: number
  aspectRatio: number
  title?: ReactNode
}

const CarouselImageItem = ({
  image,
  alt,
  imageWidth,
  aspectRatio,
  title,
}: CarouselImageItemProps) => (
  <div
    className="px-4 inline-flex flex-col items-center"
    style={{ width: `${imageWidth}px` }}
  >
    <Picture
      image={image}
      imageAlt={alt}
      imageStyle={`aspect-[${aspectRatio}]`}
    />
    {title}
  </div>
)

const Carousel = ({
  imageList,
  aspectRatio,
  desktopImageCount,
  mobileImageCount,
}: Props) => {
  const [ref, { width }] = useElementSize()
  const [imageWidth, setImageWidth] = useState(0)
  const [translateTo, setTranslateTo] = useState(0)
  const [displayCounter, setDisplayCounter] = useState(0)
  const [animationEnabled, setAnimationEnabled] = useState(false)
  const initialTouchRef = useRef<number | null>(null)
  const { width: screenWidth } = useWindowSize()

  const extraImagesRequired = useMemo(() => {
    return (screenWidth || 0) > 767 ? desktopImageCount : mobileImageCount
  }, [screenWidth, desktopImageCount, mobileImageCount])

  const setExtraNext = useCallback(() => {
    return imageList.slice(0, extraImagesRequired)
  }, [extraImagesRequired, imageList])

  const setExtraPrev = useCallback(() => {
    return imageList.slice(imageList.length - extraImagesRequired)
  }, [extraImagesRequired, imageList])

  const slideLeft = useCallback(() => {
    setDisplayCounter((prev) => prev - 1)
  }, [])

  const slideRight = useCallback(() => {
    setDisplayCounter((prev) => prev + 1)
  }, [])

  const onTransitionEnd = useCallback(() => {
    setAnimationEnabled(
      displayCounter > extraImagesRequired * -1 &&
        displayCounter < imageList.length
    )
  }, [displayCounter, extraImagesRequired, imageList])

  const touchSlide = useCallback(
    (touchEnd: number) => {
      if (initialTouchRef.current != null) {
        const delta = initialTouchRef.current - touchEnd
        if (delta > 50) {
          slideRight()
        } else if (delta < -50) {
          slideLeft()
        }
      }
      initialTouchRef.current = null
    },
    [slideLeft, slideRight]
  )

  const onTouchStart = useCallback((e: TouchEvent<HTMLElement>) => {
    initialTouchRef.current = e.changedTouches[0].screenX
  }, [])

  const onTouchEnd = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      const touchEnd = e.changedTouches[0].screenX
      touchSlide(touchEnd)
    },
    [touchSlide]
  )

  const onMouseDown = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    initialTouchRef.current = e.clientX
  }, [])

  const onMouseUp = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      touchSlide(e.clientX)
    },
    [touchSlide]
  )

  useEffect(() => {
    let itemWidth = width / mobileImageCount
    if ((screenWidth || 0) > 767) {
      itemWidth = width / desktopImageCount
    }

    setImageWidth(itemWidth)
  }, [screenWidth, width, desktopImageCount, mobileImageCount])

  useEffect(() => {
    setTranslateTo(imageWidth * (displayCounter + extraImagesRequired) * -1)
  }, [extraImagesRequired, imageWidth, displayCounter])

  useEffect(() => {
    if (!animationEnabled) {
      if (displayCounter <= extraImagesRequired * -1) {
        setDisplayCounter(imageList.length + displayCounter)
      } else if (displayCounter >= imageList.length) {
        setDisplayCounter(imageList.length - displayCounter)
      }
    }
  }, [animationEnabled, displayCounter, extraImagesRequired, imageList])

  return (
    <section className={`px-4 my-8 relative`}>
      <button
        className="absolute top-0 h-full left-0 w-4 bg-white opacity-70 hover:opacity-100 transition-opacity duration-300 flex items-center"
        onClick={slideLeft}
      >
        <ArrowLeft className="h-4 w-4" aria-label="slide left" />
      </button>
      <button
        className="absolute top-0 right-0 h-full w-4 bg-white opacity-70 hover:opacity-100 transition-opacity duration-300 flex items-center"
        onClick={slideRight}
      >
        <ArrowLeft className="h-4 w-4 rotate-180" aria-label="slide right" />
      </button>
      <section
        ref={ref}
        className="w-full overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <section
          className={`whitespace-nowrap transition-transform ${
            animationEnabled ? 'duration-300' : 'duration-[1ms]'
          }`}
          style={{ transform: `translateX(${translateTo}px)` }}
          onTransitionEnd={onTransitionEnd}
          data-testid="carousel-container"
        >
          {setExtraPrev().map(({ image, alt, title }) => (
            <CarouselImageItem
              image={image}
              alt={alt}
              imageWidth={imageWidth}
              aspectRatio={aspectRatio}
              key={`${image}_prev`}
              title={title}
            />
          ))}
          {imageList.map(({ image, alt, title }) => (
            <CarouselImageItem
              image={image}
              alt={alt}
              imageWidth={imageWidth}
              aspectRatio={aspectRatio}
              key={image}
              title={title}
            />
          ))}
          {setExtraNext().map(({ image, alt, title }) => (
            <CarouselImageItem
              image={image}
              alt={alt}
              imageWidth={imageWidth}
              aspectRatio={aspectRatio}
              key={`${image}_next`}
              title={title}
            />
          ))}
        </section>
      </section>
    </section>
  )
}

export default Carousel
